// app/frontend/components/ui/Modals/DarkModal.tsx
import React from 'react';
import { Modal } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

interface DarkModalProps {
  opened: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  size?: string;
  fullScreen?: boolean;
  withCloseButton?: boolean;
  centered?: boolean;
  yOffset?: string;
  zIndex?: number;
  /** On the modal root, e.g. wcwd-brand-zone to keep WCWD colors on a themed page. */
  className?: string;
}

export default function DarkModal({
  opened,
  onClose,
  title,
  children,
  size = 'lg',
  fullScreen = false,
  withCloseButton = true,
  centered = false,
  yOffset,
  zIndex,
  className,
}: DarkModalProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Position from below navbar: mobile navbar ~64px, desktop ~80px, plus spacing
  const navbarOffset = isMobile ? 'calc(64px + 1rem)' : 'calc(80px + 1rem)';
  const finalYOffset = yOffset ?? navbarOffset;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={size}
      title={title}
      fullScreen={fullScreen}
      withCloseButton={withCloseButton}
      centered={centered}
      yOffset={finalYOffset}
      zIndex={zIndex}
      className={className}
      styles={{
        // The content panel is a Mantine Paper, and the theme gives every
        // Paper a default bg of #17171770 (alpha .44) that Mantine applies
        // AFTER this styles API — so this backgroundColor never lands and
        // the page shows through the modal. Same cause as ThemedDrawer /
        // Spotlight (#1165). ThemedModal survives it only because it also
        // paints `body`, which is a plain div; do the same here. #1218
        content: { backgroundColor: '#2c2c2c' },
        header: {
          backgroundColor: '#1a1a1a',
          borderBottom: '1px solid #444444',
          paddingBottom: '0.75rem',
        },
        title: {
          color: '#f2f2f2',
          fontWeight: 600,
          fontSize: isMobile ? '1rem' : '1.25rem',
        },
        body: {
          backgroundColor: '#2c2c2c',
          paddingTop: '1rem',
        },
      }}
    >
      {children}
    </Modal>
  );
}
